import React, { useEffect, useState } from "react";
import { Button, Container, Content, Header, Loader, Modal } from "rsuite";
import "./Company.css";
import { useParams } from "react-router-dom";
import { get } from "lodash/fp";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { messageTypeArr, roleTypeArr } from "../../../core/config/AppConstants";
import { displayField } from "../../../utils/helpers";
import { CompanyServices } from "../../../core/services/CompanyServices";
import { Region } from "./CompanyDataTypes";
import RegionTree from "../../components/Elements/RegionTree";
function CompanyNetwork({
  companyId,
  openModal,
  setOpenModal,
}: {
  companyId: number;
  openModal: boolean;
  setOpenModal: Function;
}) {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState("");
  const [loader, setLoading] = useState(true);
  const company = useSelector((state: any) => state.company);

  const [companyData, setCompanyData] = useState<any>({});
  const [regions, setRegions] = useState<Region[]>([]);

  const [newRegionName, setNewRegionName] = useState("");
  const allowLevel: number = 2;

  async function init() {
    try {
      if (companyId) {
        const response: any = await CompanyServices.viewCompany(+companyId);
        const respData = get("data.data", response);
        setCompanyData(respData);
        initRegions();
      }
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function initRegions() {
    try {
      if (companyId) {
        setLoading(true);
        const response: any = await CompanyServices.getRegions({
          pagination: "0",
          with_groups: "1",
          with_store_count: "1",
          company: companyId,
        });
        const respData = get("data.data", response);
        setRegions(respData);
      }
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    init();
  }, [companyId]);

  useEffect(() => {}, [company.id]);

  const addRegion = () => {
    const form = new FormData();

    if (!getCompanyId()) {
      UtilityServices.showToaster({
        type: "error",
        message: t("PLEASE_SELECT_FIELD", {
          field: t("COMPANY").toLowerCase(),
        }),
      });
      return;
    }
    if (!newRegionName.trim()) {
      UtilityServices.showToaster({
        type: "error",
        message: t("REQUIRED", {
          field: t("REGION").toLowerCase() + " " + t("NAME").toLowerCase(),
        }),
      });
      return;
    }

    form.set("company_id", getCompanyId());
    form.set("region_name", newRegionName.trim());
    saveRegion(form);
  };

  const updateRegion = (regionId: any, region: any) => {
    const form = new FormData();
    form.set("region_name", region.region_name);
    saveRegion(form, regionId);
  };

  async function deleteRegion(region: any) {
    try {
      setLoading(true);
      let response: any;
      response = await CompanyServices.deleteRegion(region?.id);
      UtilityServices.showToaster({
        message: response?.data?.message,
      });
      initRegions();
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
      setErrorMessage(error?.message);
    } finally {
      setLoading(false);
    }
  }

  const addStoreGroup = (storeGroup: any) => {
    const form = new FormData();
    form.set("group_name", storeGroup.group_name);
    form.set("group_for", storeGroup.group_for);
    form.set("region_id", storeGroup.region_id);
    saveStoreGroup(form);
  };
  const updateStoreGroup = (storeGroupId: any, storeGroup: any) => {
    const form = new FormData();
    form.set("group_name", storeGroup.group_name);
    form.set("group_for", storeGroup.group_for);
    saveStoreGroup(form, storeGroupId);
  };

  async function deleteStoreGroup(storeGroup: any) {
    try {
      setLoading(true);
      let response: any;
      response = await CompanyServices.deleteStoreGroup(storeGroup?.id);
      UtilityServices.showToaster({
        message: response?.data?.message,
      });
      initRegions();
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
      setErrorMessage(error?.message);
    } finally {
      setLoading(false);
    }
  }

  async function saveRegion(form: any, id?: number) {
    try {
      setLoading(true);
      let response: any;
      if (id) {
        response = await CompanyServices.updateRegion(+id, form);
      } else {
        response = await CompanyServices.createRegion(form);
        setNewRegionName("");
      }
      UtilityServices.showToaster({
        message: response?.data?.message,
      });
      initRegions();
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
      setErrorMessage(error?.message);
    } finally {
      setLoading(false);
    }
  }

  async function saveStoreGroup(form: any, id?: number) {
    try {
      setLoading(true);
      let response: any;
      if (id) {
        response = await CompanyServices.updateStoreGroup(+id, form);
      } else {
        response = await CompanyServices.createStoreGroup(form);
      }
      UtilityServices.showToaster({
        message: response?.data?.message,
      });
      initRegions();
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
      setErrorMessage(error?.message);
    } finally {
      setLoading(false);
    }
  }

  function getCompanyId() {
    let compId = null;
    if (UtilityServices.getLoggedInUser("user_type") === roleTypeArr.client) {
      compId = UtilityServices.getLoggedInUser("company_id");
    } else if (companyId) {
      compId = companyId;
    } else if (company?.id) {
      compId = company.id;
    }
    return compId;
  }

  return (
    <div className="modal-container">
      <Modal
        open={openModal}
        backdrop={true}
        onClose={(e: any) => {
          setOpenModal("");
        }}
        size="lg"
      >
        {loader && (
          <Loader
            style={{ zIndex: 9999 }}
            size="lg"
            content={t("LOADING")}
            center
            backdrop={true}
          />
        )}
        <Modal.Header>
          <h4>
            {" "}
            {t("COMPANY") + " " + t("NETWORK") + " " + t("FOR") + " "}{" "}
            {displayField(`${companyData?.comp_name}`)}
          </h4>
        </Modal.Header>
        <Modal.Body className="pb-0 mt-3">
          <Container className="container">
            <Header className="header"></Header>
            <Content className="content customer-create-wrap mb-0">
              <div className="flex-item col-md-4 col-4">
                <input
                  type="text"
                  value={newRegionName}
                  onChange={(e) => setNewRegionName(e.target.value)}
                  className="border rounded px-2 py-1 "
                  placeholder={t("REGION") + " " + t("NAME")}
                />
                <Button
                  appearance="primary"
                  loading={loader}
                  onClick={addRegion}
                >
                  {t("ADD") + " " + t("NEW") + " " + t("REGION")}
                </Button>
              </div>
              <div>
                <h4>
                  {regions?.length ?? 0}
                  {t(" ") + t("REGIONS")}
                </h4>
                {regions.map((region) => (
                  <RegionTree
                    key={`${region.id}`}
                    region={region}
                    onDeleteRegion={deleteRegion}
                    onUpdateRegion={updateRegion}
                    onAddStoreGroup={addStoreGroup}
                    onDeleteStoreGroup={deleteStoreGroup}
                    onUpdateStoreGroup={updateStoreGroup}
                    allowLevel={allowLevel}
                    level={1}
                  />
                ))}
                {regions.length < 1 && (
                  <span>
                    {t("NO_DATA_FOUND_OF", {
                      field: t("REGION").toLowerCase(),
                    })}
                  </span>
                )}
              </div>
            </Content>
          </Container>
        </Modal.Body>
        <Modal.Footer></Modal.Footer>
      </Modal>
    </div>
  );
}

export default CompanyNetwork;
